import React, { Fragment, FunctionComponent } from 'react';
import { useTranslation } from '@rbx/intl';
import { Typography } from '@rbx/ui';
import useHeadingGroupStyles from '@modules/ui/shared/styles/HeadingGroup.styles';
import { ReferenceType } from '@modules/client';
import { functionsID, propertiesID } from '../../constants/apiReferenceConstants';
import APIReferenceDescription from '../../sharedUI/components/APIReferenceDescription';
import { GlobalReference, LibraryReference } from '../../types';
import GlobalOrLibraryCharacteristicsIterator from './GlobalOrLibraryCharacteristicsIterator';
import ReferenceSummary from '../../sharedUI/components/ReferenceSummary';
import ReferenceSummaryV2 from '../../sharedUI/components/ReferenceSummaryV2';
import GlobalOrLibraryMemberDetails from './GlobalOrLibraryMemberDetails';

export interface GlobalOrLibraryContentProps {
  globalOrLibraryReference: GlobalReference | LibraryReference;
  referenceType: ReferenceType;
  engineRevampEnabled?: boolean;
}

const GlobalOrLibraryContent: FunctionComponent<
  React.PropsWithChildren<GlobalOrLibraryContentProps>
> = ({ globalOrLibraryReference, referenceType, engineRevampEnabled = false }) => {
  const { translate } = useTranslation();
  const {
    classes: { root },
  } = useHeadingGroupStyles();
  const { name, description, codeSamples, properties, functions } = globalOrLibraryReference;

  if (engineRevampEnabled) {
    return (
      <div className={root}>
        <ReferenceSummaryV2 reference={globalOrLibraryReference} referenceType={referenceType} />
        <APIReferenceDescription
          name={name}
          description={description}
          codeSamples={codeSamples}
          engineRevampEnabled
        />
        <GlobalOrLibraryMemberDetails
          properties={properties ?? []}
          functions={functions ?? []}
          referenceType={referenceType}
        />
      </div>
    );
  }

  return (
    <div className={root}>
      <APIReferenceDescription name={name} description={description} codeSamples={codeSamples} />
      <ReferenceSummary reference={globalOrLibraryReference} referenceType={referenceType} />
      {functions && functions.length > 0 && (
        <Fragment>
          <Typography variant='h2' component='h2' id={functionsID}>
            {translate('Label.Functions') || 'Functions'}
          </Typography>
          <GlobalOrLibraryCharacteristicsIterator rows={functions} />
        </Fragment>
      )}
      {properties && properties.length > 0 && (
        <Fragment>
          <Typography variant='h2' component='h2' id={propertiesID}>
            {translate('Label.Properties') || 'Properties'}
          </Typography>
          <GlobalOrLibraryCharacteristicsIterator rows={properties} />
        </Fragment>
      )}
    </div>
  );
};
export default GlobalOrLibraryContent;
